import { useEffect, useState } from 'react';
import { Link as LinkIcon, Loader2, CheckCircle2, Unplug, AlertCircle, Calendar, RefreshCw } from 'lucide-react';
import { fetchCalendarConnection, startCalendarOAuth, disconnectCalendar, fetchGcalLastSync, triggerGcalSyncMe } from '../../lib/api';
import { toast } from 'sonner';

/**
 * CalendarConnect — pannello in /legenda per connettere/disconnettere
 * il Google Calendar dell'utente.
 *
 * Flusso:
 *   1. Carica stato via GET /api/calendar/connection
 *   2. "Connetti": GET /api/oauth/calendar/start → window.location.href = url
 *   3. Google redirect → /api/oauth/calendar/callback (backend) → redirect a /legenda?calendar_connected=1
 *   4. Re-fetch stato e mostra "Connesso a xxx@gmail.com"
 *
 * Mostra anche errore esplicito se backend non configurato (configured=false)
 * con istruzioni minime per l'utente che gestisce le env.
 */
export default function CalendarConnect() {
  const [conn, setConn] = useState(null);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [lastSync, setLastSync] = useState(null);
  const [syncing, setSyncing] = useState(false);

  const runManualSync = async () => {
    if (syncing) return;
    setSyncing(true);
    try {
      const res = await triggerGcalSyncMe();
      const s = res?.result || {};
      const okTotal = (s.mit_ok || 0) + (s.az_ok || 0);
      const failTotal = (s.mit_fail || 0) + (s.az_fail || 0);
      if (s.skipped) {
        toast.warning(`Sync saltato: ${s.reason || 'motivo sconosciuto'}`);
      } else if (failTotal > 0) {
        toast.warning(`Sync completato · ${okTotal} OK · ${failTotal} fallite`);
      } else {
        toast.success(`Sync completato · ${okTotal} eventi aggiornati`);
      }
      // Ricarica lo stato per aggiornare il widget "Ultimo sync"
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Sync fallito');
    } finally {
      setSyncing(false);
    }
  };

  const reload = async () => {
    try {
      const d = await fetchCalendarConnection();
      setConn(d);
      // Fetch last sync solo se connesso — best-effort (tabella potrebbe non esistere ancora).
      if (d?.connected) {
        try {
          const ls = await fetchGcalLastSync();
          setLastSync(ls?.last_sync || null);
        } catch { setLastSync(null); }
      }
    } catch {
      setConn({ connected: false, configured: false });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    reload();
    // Intercetta i query param post-callback
    const params = new URLSearchParams(window.location.search);
    if (params.get('calendar_connected')) {
      toast.success('Google Calendar collegato');
      // Pulisci URL
      window.history.replaceState({}, '', window.location.pathname);
    }
    const err = params.get('calendar_error');
    if (err) {
      toast.error(`Errore connessione calendario: ${err}`);
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  const handleConnect = async () => {
    setConnecting(true);
    try {
      const { authorization_url } = await startCalendarOAuth();
      window.location.href = authorization_url;
    } catch (e) {
      setConnecting(false);
      toast.error(e?.response?.data?.detail || 'Errore avvio OAuth');
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm('Disconnettere Google Calendar? Gli eventi già creati resteranno su Google.')) return;
    try {
      await disconnectCalendar();
      toast.success('Calendario disconnesso');
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore disconnessione');
    }
  };

  if (loading) {
    return (
      <div className="flex items-start justify-between py-4 border-b border-linesoft">
        <div className="flex-1 mono-label">Verifico connessione calendario…</div>
      </div>
    );
  }

  return (
    <div className="flex items-start justify-between py-4 border-b border-linesoft" data-testid="calendar-connect-row">
      <div className="flex-1 pr-6">
        <div className="font-display text-base mb-1 inline-flex items-center gap-2">
          <Calendar size={16} strokeWidth={1.5} />
          Google Calendar
          {conn?.connected && <SyncStatusBadge lastSync={lastSync} />}
        </div>
        {conn?.connected ? (
          <p className="font-body text-sm text-soft leading-relaxed max-w-md">
            Collegato a <strong className="text-ink">{conn.google_email}</strong>.
            Ogni MIT e azione settimanale che crei viene aggiunta come evento
            all-day sul tuo calendario primario.
            {conn.last_sync_at && (
              <span className="block mt-1 font-mono text-[10px] uppercase tracking-[0.15em] text-mute">
                Ultimo push evento: {new Date(conn.last_sync_at).toLocaleString('it-IT')}
              </span>
            )}
            {lastSync && (
              <span
                className="block mt-1 font-mono text-[10px] uppercase tracking-[0.15em] text-mute"
                data-testid="gcal-last-sync-widget"
                title={lastSync.reason || undefined}
              >
                Ultimo sync automatico: {new Date(lastSync.ran_at).toLocaleString('it-IT')}
                {' · '}
                {lastSync.skipped
                  ? <span className="text-amber-700">saltato ({lastSync.reason || 'motivo sconosciuto'})</span>
                  : (
                    <>
                      <span className="text-ok">
                        {(lastSync.mit_ok || 0) + (lastSync.az_ok || 0)} push OK
                      </span>
                      {((lastSync.mit_fail || 0) + (lastSync.az_fail || 0)) > 0 && (
                        <>
                          {' · '}
                          <span className="text-risk">
                            {(lastSync.mit_fail || 0) + (lastSync.az_fail || 0)} fail
                          </span>
                        </>
                      )}
                    </>
                  )}
              </span>
            )}
          </p>
        ) : conn?.configured ? (
          <p className="font-body text-sm text-soft leading-relaxed max-w-md">
            Collega un account Google per pushare automaticamente MIT e azioni
            settimanali sul tuo calendario. Sync solo in uscita: modifiche su
            Google NON tornano qui.
          </p>
        ) : (
          <div className="flex items-start gap-2 text-sm text-amber-700 bg-amber-50/60 border border-amber-200 rounded px-3 py-2 max-w-md">
            <AlertCircle size={16} className="mt-0.5 shrink-0" />
            <span>
              Integrazione non ancora configurata. Servono <code>GOOGLE_CLIENT_ID</code> e
              <code> GOOGLE_CLIENT_SECRET</code> nelle env del backend.
            </span>
          </div>
        )}
      </div>

      {conn?.configured && (
        <div className="shrink-0 flex flex-col gap-2 items-end">
          {conn.connected && (
            <button
              onClick={runManualSync}
              disabled={syncing}
              className="inline-flex items-center gap-2 px-4 py-2 border border-line font-mono text-xs uppercase tracking-[0.18em] text-ink hover:border-accent hover:text-accent transition-colors disabled:opacity-50"
              data-testid="calendar-sync-now-btn"
              title="Forza sync ora (recupera task pushate a metà)"
            >
              {syncing
                ? <Loader2 size={14} className="animate-spin" strokeWidth={1.5} />
                : <RefreshCw size={14} strokeWidth={1.5} />}
              {syncing ? 'Sync in corso...' : 'Sincronizza ora'}
            </button>
          )}
          {conn.connected ? (
            <button
              onClick={handleDisconnect}
              className="inline-flex items-center gap-2 px-4 py-2 border border-line font-mono text-xs uppercase tracking-[0.18em] text-soft hover:border-ink hover:text-ink transition-colors"
              data-testid="calendar-disconnect-btn"
            >
              <Unplug size={14} strokeWidth={1.5} />
              Disconnetti
            </button>
          ) : (
            <button
              onClick={handleConnect}
              disabled={connecting}
              className="inline-flex items-center gap-2 px-4 py-2 bg-ink text-canvas font-mono text-xs uppercase tracking-[0.18em] hover:bg-accent transition-colors disabled:opacity-50"
              data-testid="calendar-connect-btn"
            >
              {connecting ? <Loader2 size={14} className="animate-spin" /> : <LinkIcon size={14} strokeWidth={1.5} />}
              Connetti
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// Mini-helper esportato per quick-check sticker connesso (riusabile altrove)
export function ConnectedSticker({ email }) {
  return (
    <span className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.18em] text-ok">
      <CheckCircle2 size={12} strokeWidth={1.8} />
      Calendar: {email}
    </span>
  );
}

/**
 * SyncStatusBadge — pallino colorato che riassume lo stato dell'ultimo sync GCal.
 *
 *   • Grigio: nessun sync registrato (tabella `gcal_sync_log` vuota per l'utente)
 *   • Verde:  ultimo sync < 24h fa e nessun fallimento (mit_fail + az_fail == 0)
 *   • Giallo: sync avvenuto ma con almeno un fallimento, o `skipped=true`,
 *             o più vecchio di 24h (potenziale problema di scheduler / connessione)
 *
 * Non-interattivo: hover mostra tooltip con dettagli. La riga esplicita
 * "Ultimo sync automatico" resta comunque sotto per chi vuole leggere i numeri.
 */
function SyncStatusBadge({ lastSync }) {
  let color = 'bg-line';
  let label = 'Mai sincronizzato';
  let tooltip = 'Nessun sync automatico registrato ancora. Il job gira due volte al giorno (06:00 e 18:00 Europe/Rome), oppure puoi forzarlo con "Sincronizza ora".';
  if (lastSync?.ran_at) {
    const ran = new Date(lastSync.ran_at);
    const ageMs = Date.now() - ran.getTime();
    const ageHours = ageMs / 36e5;
    const failTotal = (lastSync.mit_fail || 0) + (lastSync.az_fail || 0);
    const okTotal = (lastSync.mit_ok || 0) + (lastSync.az_ok || 0);
    const stamp = ran.toLocaleString('it-IT');
    if (lastSync.skipped) {
      color = 'bg-amber-500';
      label = 'Sync saltato';
      tooltip = `${stamp} · saltato (${lastSync.reason || 'motivo sconosciuto'})`;
    } else if (failTotal > 0) {
      color = 'bg-amber-500';
      label = `${failTotal} fallimenti`;
      tooltip = `${stamp} · ${okTotal} OK · ${failTotal} fail`;
    } else if (ageHours > 24) {
      color = 'bg-amber-500';
      label = 'Vecchio';
      tooltip = `${stamp} · ${okTotal} OK · ultimo sync oltre 24h fa`;
    } else {
      color = 'bg-ok';
      label = 'OK';
      tooltip = `${stamp} · ${okTotal} push OK`;
    }
  }
  return (
    <span
      className="inline-flex items-center gap-1 font-mono text-[9px] uppercase tracking-[0.18em] text-mute"
      title={tooltip}
      data-testid="gcal-sync-status-badge"
    >
      <span className={`inline-block w-2 h-2 rounded-full ${color}`} aria-hidden />
      {label}
    </span>
  );
}
